'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import { ShowcaseCopyContext, translateShowcaseNode } from '../translation';
import { useReducedMotion, useSequenceClock } from '../use-animation-clock';
import { T, productionFacts } from './workflow-facts';
import { $d } from './sell-market-animation';
import { Of, Nf, Pf, jf, ProductionStepRail } from './deliverables-animation';
import {
  ProductionArtifact,
  ProductionProgress,
  ProductionBadge,
  TechpackTabs,
  ProductionConversation,
  ProductionPrompt,
  ProductionWorkingStatus,
} from './production-primitives';
import {
  Qm,
  EvidenceLegend,
  Zm,
  MeasurementTable,
  BillOfMaterials,
  ColorwayTable,
} from './techpack-tables';
import { TechpackSchematic, TechpackOpenItems } from './techpack-schematic';
import { TechpackMetadata, SampleRedlines } from './sample-redlines';
import { CollectionReview } from './collection-review';
import { CollectionFreezePacket } from './collection-freeze-packet';
import { SourcingPlan, SupplierRFQ } from './sourcing-plan';
import { ReadinessReview } from './readiness-review';
import { FactoryHandoff } from './factory-handoff';
import { useMostlyInView, ShowcaseIcon } from './scene-primitives';
import { ProductionIcon } from './deliverable-previews';
export function DetailedTechpack({ t: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let t = e >= 6300 ? 2 : +(e >= 4100),
    n = Math.round(T.componentImages.done * $d(e, Of[0] ?? 0, 6400));
  return (
    <ProductionArtifact
      icon={'ruler'}
      title={translateShowcaseNode(showcaseCopy, 'Box Tote Tech Pack')}
      view={Qm[t]}
      footer={
        <React.Fragment>
          <div className={'min-w-0 flex-1'}>
            <ProductionProgress
              value={n / T.componentImages.of}
              label={'Component images'}
              meta={`${n}/${T.componentImages.of} done`}
            />
          </div>
          <ProductionBadge tone={'amber'} className={'ml-2 max-sm:hidden'}>
            {translateShowcaseNode(showcaseCopy, 'Draft for technical review')}
          </ProductionBadge>
        </React.Fragment>
      }
    >
      <div
        className={
          'grid gap-4 p-3 sm:p-4 md:grid-cols-[minmax(0,1.08fr)_minmax(0,1fr)] md:gap-5'
        }
      >
        <figure
          className={
            'pd-sheet relative m-0 flex flex-col rounded-[10px] p-3 shadow-[inset_0_0_0_1px_var(--line)] sm:p-4'
          }
        >
          <figcaption
            className={
              'text-ink-3 flex items-center justify-between gap-2 font-mono text-[11px]'
            }
          >
            <span>
              {translateShowcaseNode(showcaseCopy, 'tote-front-and-side')}
            </span>
            <span className={'num'}>
              {translateShowcaseNode(showcaseCopy, 'cm \xB7 1:5')}
            </span>
          </figcaption>
          <div className={'mt-1 px-1 sm:px-2'}>
            <TechpackSchematic t={e} />
          </div>
          <div className={'mt-2 flex items-end justify-between gap-2'}>
            <EvidenceLegend />
          </div>
          <span className={'sr-only'}>
            {translateShowcaseNode(
              showcaseCopy,
              'Flat sketch of the box tote, front and side views, with points of measure A to E.'
            )}
          </span>
        </figure>
        <div className={'flex min-w-0 flex-col gap-3'}>
          <TechpackTabs tabs={Zm} active={t} />
          <div className={'grid min-w-0'}>
            <div
              key={t}
              className={'pd-enter min-w-0'}
              style={{
                gridArea: '1 / 1',
              }}
            >
              {translateShowcaseNode(
                showcaseCopy,
                t === 0 ? (
                  <MeasurementTable t={e} />
                ) : t === 1 ? (
                  <BillOfMaterials t={e} />
                ) : (
                  <ColorwayTable t={e} />
                )
              )}
            </div>
          </div>
        </div>
        <div className={'flex flex-col gap-3 md:col-span-2'}>
          <TechpackMetadata t={e} />
          <TechpackOpenItems t={e} />
          <p className={'text-ink-4 text-[12.5px]'}>
            {translateShowcaseNode(showcaseCopy, T.status)}
          </p>
        </div>
      </div>
    </ProductionArtifact>
  );
}
export const productionStages = {
  review: CollectionReview,
  lock: CollectionFreezePacket,
  techpack: DetailedTechpack,
  sourcing: SourcingPlan,
  rfq: SupplierRFQ,
  sample: SampleRedlines,
  readiness: ReadinessReview,
  handoff: FactoryHandoff,
};
export function ProductionStage({ children: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let t = React.useRef(null),
    [n, r] = React.useState(null);
  return (
    React.useLayoutEffect(() => {
      let e = t.current;
      if (!e) return;
      let n = () => r(e.offsetHeight);
      n();
      let i = new ResizeObserver(n);
      return (i.observe(e), () => i.disconnect());
    }, []),
    (
      <div
        className={'pd-stage -m-1.5 min-w-0 overflow-hidden p-1.5'}
        style={{
          height: n === null ? void 0 : n + 12,
        }}
      >
        <div ref={t}>{translateShowcaseNode(showcaseCopy, e)}</div>
      </div>
    )
  );
}
export function ProductionAnimation() {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let e = useReducedMotion(),
    [t, n] = useMostlyInView(0.55),
    [r, i] = React.useState('review'),
    [a, o] = React.useState(!0),
    [s, c] = React.useState(!0),
    l = Nf.findIndex((e) => e.key === r),
    u = Pf(l),
    d = u.ms + jf,
    p = useSequenceClock({
      key: r,
      duration: d,
      running: n === !0 && s,
      reduced: e,
    }),
    { done: m, restart: h } = p,
    g = p.t;
  React.useEffect(() => {
    if (!a || e || !m) return;
    const nextStep = Nf[(l + 1) % Nf.length];
    if (!nextStep) return;
    const frame = requestAnimationFrame(() => i(nextStep.key));
    return () => cancelAnimationFrame(frame);
  }, [a, e, m, l]);
  let ee = (e) => {
      (o(!1), c(!0), e === r ? h() : i(e));
    },
    te = () => {
      if (s && a) {
        c(!1);
        return;
      }
      (o(!0), c(!0), m && h());
    },
    Component_ne = productionStages[u.key],
    b = s && a && !e;
  return (
    <div ref={t} className={'sf mt-12 rounded-[20px] p-2 md:mt-14'}>
      <div className={'flex items-end gap-2 px-1 pt-1'}>
        <div className={'min-w-0 flex-1'}>
          <ProductionStepRail
            value={r}
            onChange={ee}
            progress={a && !e ? Math.min(1, g / d) : null}
          />
        </div>
        <div className={'flex shrink-0 items-center gap-0.5 pb-1'}>
          {translateShowcaseNode(
            showcaseCopy,
            e ? null : (
              <button
                type={'button'}
                onClick={te}
                aria-label={translateShowcaseNode(
                  showcaseCopy,
                  b ? 'Pause the tour' : 'Play the tour'
                )}
                className={'ghost press grid size-8 place-items-center'}
              >
                <ShowcaseIcon
                  name={b ? 'pause' : 'play'}
                  className={'size-4'}
                />
              </button>
            )
          )}
          <button
            type={'button'}
            onClick={h}
            aria-label={translateShowcaseNode(showcaseCopy, 'Replay this step')}
            className={
              'ghost press grid size-8 place-items-center max-sm:hidden'
            }
          >
            <ShowcaseIcon name={'replay'} className={'size-4'} />
          </button>
        </div>
      </div>
      <p className={'sr-only'}>
        {translateShowcaseNode(showcaseCopy, 'Step ')}
        {translateShowcaseNode(showcaseCopy, l + 1)}
        {translateShowcaseNode(showcaseCopy, ' of ')}
        {translateShowcaseNode(showcaseCopy, Nf.length)}
        {translateShowcaseNode(showcaseCopy, ': ')}
        {translateShowcaseNode(showcaseCopy, u.workflow)}
      </p>
      <div
        className={
          'mt-2 rounded-[14px] bg-[var(--canvas)] p-3 shadow-[inset_0_0_0_1px_var(--line)] md:p-5'
        }
      >
        <div
          className={'grid gap-4 lg:grid-cols-[288px_minmax(0,1fr)] lg:gap-6'}
        >
          <div className={'max-lg:hidden'}>
            <ProductionConversation step={u} t={g} />
          </div>
          <div className={'flex min-w-0 flex-col gap-2 lg:hidden'}>
            <ProductionPrompt step={u} />
            <ProductionWorkingStatus step={u} t={g} />
          </div>
          <ProductionStage>
            <div key={u.key} className={'pd-enter'}>
              <Component_ne t={g} />
            </div>
          </ProductionStage>
        </div>
      </div>
      <p
        className={
          'text-ink-4 flex gap-2 px-3 pt-3 pb-2 text-[12.5px] leading-[1.5] text-pretty'
        }
      >
        <ProductionIcon name={'info'} className={'mt-0.5 size-3.5 shrink-0'} />
        {translateShowcaseNode(showcaseCopy, productionFacts.caveat)}
      </p>
    </div>
  );
}
